import { useCallback, useMemo, type CSSProperties, type KeyboardEvent, type PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import { modeColor } from '../../styles';
import { Box, HStack } from '../Core';
import { DEFAULT_LOOKBACK_OPTIONS, DateRangePicker } from '../DateRangePicker';
import { Divider } from '../Divider';
import { FilterBuilder, FilterBuilderClearAllButton, FilterBuilderToggleButton } from '../Filters/FilterBuilder';
import { FormControlSizes, Input, SearchSelect } from '../Form';
import { Icon, IconName } from '../Icons';
import { Portal } from '../Portal';

import { useMixpanel } from '../../contexts/MixpanelContext';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { MixpanelEvent } from '../../tokens/mixpanel';
import { AccordionElement } from '../Accordion/AccordionElement';
import { Tooltip } from '../Tooltip';
import type { BlotterTableFiltersProps } from './BlotterTableFilters.types';
import { BlotterTablePauseButton } from './BlotterTablePauseButton';

export const BLOTTER_TABLE_FILTERS_CONTAINER_ID = 'filtersContainer';

function AccordionBodyMaybePortal({ portalId, children }: PropsWithChildren<{ portalId?: string }>) {
  if (portalId != null) {
    return <Portal portalId={portalId}>{children}</Portal>;
  }
  return <>{children}</>;
}

export function BlotterTableFilters<TTimestampField extends string = string>({
  quickFilterPrefix,
  prefix,
  suffix,

  accordionBodyPrefix,

  showQuickFilter = true,
  showQuickFilterOnly = false,
  quickFilterPlaceholder,
  quickFilterText,
  onQuickFilterTextChanged,

  showFilterBuilder = true,
  showDateRangePicker = true,
  dateRangePickerClearable = true,

  selectedTimestampField: inputSelectedTimestampField,
  timestampFields,
  dateRange,
  dateRangeShortcuts = DEFAULT_LOOKBACK_OPTIONS,
  onDateRangeChanged = () => {},
  onTimestampFieldChanged = () => {},

  filterBuilder,
  accordion,
  canSubmitClearedDateRange = false,
  portalId,
  accordionBodyPortalId,

  pause,
  paused,
  resume,
  showPauseButton,
  suppressClearAllFiltersButton = false,

  size = FormControlSizes.Small,
  gap = 'spacing4',
  rowEndSpacing = 'spacing16',
}: BlotterTableFiltersProps<TTimestampField>) {
  const mixpanel = useMixpanel();

  const quickFilterInputWidth: CSSProperties['width'] = showQuickFilterOnly
    ? '100%'
    : size <= FormControlSizes.Small
      ? '120px'
      : '240px';

  const handleOnFilterTextChanged: React.ChangeEventHandler<HTMLInputElement> = useCallbackRef(e => {
    mixpanel.track(MixpanelEvent.FilterQuickSearch);
    onQuickFilterTextChanged?.(e.target.value);
  });

  const handleQuickFilterKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Keep Enter from submitting an enclosing EntityAdmin drawer form.
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const handleOnDateRangeChanged: typeof onDateRangeChanged = useCallbackRef((...args) => {
    mixpanel.track(MixpanelEvent.ChangeTimeFilter);
    onDateRangeChanged(...args);
  });

  const defaultTimestampField = useMemo(() => timestampFields?.find(tsf => tsf.default)?.Name, [timestampFields]);
  const selectedTimestampField = inputSelectedTimestampField ?? defaultTimestampField;

  const timestampFieldDescription = useMemo(() => {
    return (
      selectedTimestampField != null &&
      timestampFields?.find(timestampField => timestampField.Name === selectedTimestampField)?.Description
    );
  }, [selectedTimestampField, timestampFields]);

  const timestampFieldOptions = useMemo(() => {
    return timestampFields?.map(tsf => tsf.Name);
  }, [timestampFields]);

  const getTimestampFieldLabel = useCallback(
    (tsf: TTimestampField) => {
      return timestampFields?.find(field => field.Name === tsf)?.DisplayName ?? tsf;
    },
    [timestampFields]
  );

  const handleTimestampFieldChange = useCallbackRef((newValue: TTimestampField | undefined) => {
    if (newValue != null) {
      mixpanel.track(MixpanelEvent.FilterTimeTypeChanged);
      onTimestampFieldChanged(newValue);
    }
  });

  const dateRangePickerOptions = useMemo(() => {
    return dateRangeShortcuts.map(shortcut => ({
      value: shortcut,
    }));
  }, [dateRangeShortcuts]);

  // When using a RestBlotterTable, the pause and resume props are not available.
  // The type of the props is `Partial<BlotterTablePauseProps>`, so we need to check if they are defined.
  const hasPauseResumeProps = useMemo(() => pause != null && resume != null && paused != null, [pause, resume, paused]);

  const handleClosed = accordion?.onClosed;
  const handleOpened = accordion?.onOpened;
  const canRenderFilterBuilder =
    showFilterBuilder && filterBuilder != null && accordion != null && filterBuilder.propertiesList.length > 0;

  return (
    <>
      <Portal portalId={portalId ?? BLOTTER_TABLE_FILTERS_CONTAINER_ID}>
        <HStack justifyContent="space-between" gap={gap}>
          {quickFilterPrefix}
          {quickFilterPrefix && showQuickFilter && <Divider orientation="vertical" m="spacing4" />}
          {showQuickFilter && (
            <Input
              prefix={<Icon icon={IconName.Search} />}
              clearable={true}
              size={size}
              width={quickFilterInputWidth}
              placeholder={quickFilterPlaceholder}
              value={quickFilterText}
              onChange={handleOnFilterTextChanged}
              onKeyDown={handleQuickFilterKeyDown}
              data-testid="filter-search"
            />
          )}
          {showQuickFilter && prefix && <Divider orientation="vertical" m="spacing4" />}
          {prefix}
          {prefix && (canRenderFilterBuilder || (showDateRangePicker && dateRange != null)) && (
            <Divider orientation="vertical" m="spacing4" />
          )}
          {canRenderFilterBuilder && (
            <>
              {!suppressClearAllFiltersButton && filterBuilder.activeFilterCount > 0 && (
                <FilterBuilderClearAllButton
                  removeAllFilterClauses={filterBuilder.removeAllFilterClauses}
                  disabled={filterBuilder.activeFilterCount === 0}
                  size={size}
                />
              )}
              <FilterBuilderToggleButton
                activeFilterCount={filterBuilder.activeFilterCount}
                isOpen={accordion.isOpen}
                size={size}
                onClick={() => accordion.toggle()}
              />
            </>
          )}
          {showDateRangePicker && dateRange != null && (
            <>
              {timestampFieldOptions && timestampFieldOptions?.length > 0 && (
                <Tooltip tooltip={timestampFieldDescription} usePortal>
                  <SearchSelect
                    data-testid="timestamp-search"
                    selection={selectedTimestampField}
                    options={timestampFieldOptions}
                    getLabel={getTimestampFieldLabel}
                    onChange={handleTimestampFieldChange}
                    size={size}
                    style={{ width: '125px' }}
                    showDropdownSearch={false}
                    portalize
                  />
                </Tooltip>
              )}
              <Box w="200px">
                <DateRangePicker
                  size={size}
                  onChange={handleOnDateRangeChanged}
                  value={dateRange}
                  shortcuts={dateRangePickerOptions}
                  clearable={dateRangePickerClearable}
                  canSubmitCleared={canSubmitClearedDateRange}
                />
              </Box>
            </>
          )}
          {showPauseButton && hasPauseResumeProps && (
            <>
              <Divider orientation="vertical" mx="spacing4" />
              <BlotterTablePauseButton pause={pause!} paused={paused!} resume={resume!} />
            </>
          )}
          {suffix && <Divider orientation="vertical" m="spacing4" />}
          {suffix}
        </HStack>
      </Portal>
      {!canRenderFilterBuilder && accordionBodyPrefix != null && (
        <AccordionBodyMaybePortal portalId={accordionBodyPortalId}>
          <AccordionBodyRow rowEndSpacing={rowEndSpacing}>{accordionBodyPrefix}</AccordionBodyRow>
        </AccordionBodyMaybePortal>
      )}
      {canRenderFilterBuilder && (
        <AccordionBodyMaybePortal portalId={accordionBodyPortalId}>
          <AccordionElement
            isOpen={accordion.isOpen}
            onClosed={handleClosed}
            onOpened={handleOpened}
            initialOpen={accordion.initialOpen}
            w="100%"
          >
            <AccordionBodyRow rowEndSpacing={rowEndSpacing}>
              {accordionBodyPrefix && (
                <>
                  {accordionBodyPrefix}
                  <Divider orientation="vertical" />
                </>
              )}
              <FilterBuilder {...filterBuilder} />
            </AccordionBodyRow>
          </AccordionElement>
        </AccordionBodyMaybePortal>
      )}
    </>
  );
}

function AccordionBodyRow({
  rowEndSpacing,
  children,
}: PropsWithChildren<{ rowEndSpacing: BlotterTableFiltersProps['rowEndSpacing'] }>) {
  const theme = useTheme();

  return (
    <HStack
      w="100%"
      justifyContent="flex-start"
      gap="spacing16"
      p="spacing16"
      px={rowEndSpacing}
      py="spacing8"
      background={modeColor(theme, theme.colors.gray['030'], theme.colors.gray['020'])}
      borderBottom="solid 1px"
      borderColor="gray.010"
    >
      {children}
    </HStack>
  );
}
